// Copyright 2018 Sorama B.V.

import { bindable } from "aurelia-framework";
import { IPlotParent } from "controls/plot/IPlotParent";
import { Margin, Pair, Rect, Size } from "controls/plot/IPlotPart";
import { PlotPartBase } from "controls/plot/PlotPartBase";
import { IExportable } from "libraries/Export/IExportable";
import { SVGNode } from "libraries/SVG/SVGNode";
import { Unit } from "units/Unit";

export interface LegendGroup {
    title?: string;
    entries: LegendEntry[];
}

export interface LegendEntry {
    name: string;
    color?: string;
}

export interface PositionedLegendGroup extends LegendGroup {
    position: number;
}

export class LegendPart extends PlotPartBase implements IExportable {
    //#region HTMLElements
    private legendSvgElement: SVGGElement;
    //#endregion

    //#region fields
    @bindable public legend: boolean;
    @bindable public fillColor = "white";
    @bindable public fillOpacity: number;
    @bindable public clipPath: string;
    @bindable public legendBoxWidth: number;
    @bindable public legendBoxHeight = 30;
    @bindable public legendBoxIndentation = 0;
    @bindable public legendBoxNextLine = 0;
    @bindable public lineEndMargin = 0;

    @bindable({ changeHandler: "positionChanged" })
    public position: boolean;

    @bindable public legendGroups: LegendGroup[];
    @bindable private positionedLegendGroups: PositionedLegendGroup[];

    private positionLegend = 0;
    private legendBoxEntries = 6;
    private viewBox = "0 0 1 1";
    //#endregion

    //#region constants
    private legendBoxOuterMargin = 10;
    private legendBoxInnerMargin = 10;
    //#endregion

    //#region Constructor and Initialisation
    constructor() {
        super(0);
    }

    public bind(context: IPlotParent): void {
        super.bind(context);
    }

    public onMeasureMargin(): Margin {
        // No extra margin required
        return new Margin(0, 0, 0, 0);
    }

    public onSize(size: Size, margin: Margin): void {
        if (size.width - margin.left - margin.right <= 0 || size.height - margin.top - margin.bottom <= 0) return;

        this.size = size;
        this.margin = margin;
        this.actualSize = new Size(size.width - margin.left - margin.right, size.height - margin.top - margin.bottom);

        this.viewBox = -margin.left + " " + -margin.top + " " + this.size.width + " " + this.size.height;

        this.positionChanged();
    }

    public onViewPortComputed(viewPort: Rect, viewPortBounds: Rect, units: Pair<Unit>) {
        super.onViewPortComputed(viewPort, viewPortBounds, units);
    }
    //#endregion

    //#region IExportable

    public exportToSvg(): Promise<SVGNode> {
        if (!this.legendSvgElement) return undefined;

        const svgs = SVGNode.getSvgsInElement(this.legendSvgElement);
        if (svgs.length != 1) return undefined; // Only 1 expected..

        return Promise.resolve(svgs[0]);
    }

    //#endregion

    //region changeHandler
    public positionChanged(): void {
        if (this.position) {
            this.positionLegend = this.legendBoxOuterMargin;
        } else if (this.actualSize) {
            this.positionLegend = this.actualSize.width - this.legendBoxWidth - this.legendBoxOuterMargin;
        }
    }

    public legendGroupsChanged(): void {
        if (!this.legendGroups) {
            this.positionedLegendGroups = [];
            return;
        }

        this.positionedLegendGroups = this.legendGroups.map((x, i) => ({
            title: x.title,
            entries: x.entries,
            position: this.computeYPosition(i),
        }));
        this.legendBoxEntries = this.legendGroups.filter((x) => x.title).length;
        this.legendGroups.map((x): void => {
            this.legendBoxEntries += x.entries.filter((x) => x.name).length;
        });
    }
    //#endregion

    private computeYPosition(index: number): number {
        let offset = 0;

        for (let i = 0; i < index; i++) {
            offset += this.legendGroups[i].entries.length + 1;
        }
        return offset;
    }
}
